import { type Common } from './common.js';
import { type RpaJob } from './models/rpaJob.js';
import {
  type CreateValues,
  type DisplayableWorkflow,
  type EncounterWorkflow,
  type FindAllOptions,
  type Kind,
  type Status,
  type ViewOptions,
  type ViewWorkflow,
  type WorkflowV2,
} from './models/workflow.js';

export class API {
  common: Common;
  baseEndpoint = '/workflows';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll<T extends EncounterWorkflow | DisplayableWorkflow = DisplayableWorkflow>(options?: {
    encounterId?: string;
    ehrInformationId?: string;
    patientOrderId?: string;
    kind?: Kind;
    status?: Status;
    isCurrent?: boolean;
  }): Promise<T[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllCurrent(options: FindAllOptions): Promise<DisplayableWorkflow[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/current`, options);
    const { workflows } = await this.common.getJSON(url);
    return workflows;
  }

  async findAllCurrentCounts(
    options: FindAllOptions
  ): Promise<{ [kind: string]: Record<string, number> }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/current/counts`, options);
    const { counts } = await this.common.getJSON(url);
    return counts;
  }

  async findAllView(options: ViewOptions = {}): Promise<ViewWorkflow[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/view`, options);
    return await this.common.getJSON(url);
  }

  async findAllViewKinds(): Promise<Kind[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/view/kinds`);
    return await this.common.getJSON(url);
  }

  async findAllRpaJobsById(id: string): Promise<RpaJob[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/rpa-jobs`);
    return await this.common.getJSON(url);
  }

  async create(args: CreateValues): Promise<WorkflowV2> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, args);
  }

  async retry(args: {
    kind: Kind;
    ehrInformationId?: string;
    encounterId?: string;
    patientOrderId?: string;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/retry`);
    return await this.common.postJSON(url, args);
  }
}
